import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trophy, Plus, Pencil, Trash2, Lock, Unlock, Loader2, Calendar, Crown, Award, Mail, Send, Sparkles, Download, Share2 } from "lucide-react";

const KINDS = [
  { key: "artist", label: "Artiste" },
  { key: "track",  label: "Morceau / Clip" },
];

export const AdminTopMadaPanel = () => {
  const [cats, setCats] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  // form catégorie
  const [showCatForm, setShowCatForm] = useState(false);
  const [catLabel, setCatLabel] = useState("");
  const [catSlug, setCatSlug] = useState("");
  const [catKind, setCatKind] = useState("artist");
  const [catOrder, setCatOrder] = useState(100);
  // form saison
  const [seasonLabel, setSeasonLabel] = useState("");
  const [seasonEnd, setSeasonEnd] = useState("");

  const load = async () => {
    try {
      const [c, s, st] = await Promise.all([
        api.get("/admin/topmada/categories"),
        api.get("/admin/topmada/seasons"),
        api.get("/admin/topmada/stats"),
      ]);
      setCats(c.data.categories || []);
      setSeasons(s.data.seasons || []);
      setCurrentSeason(s.data.current || null);
      setStats(st.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    }
  };
  useEffect(() => { load(); }, []);

  // ── Catégories ──
  const createCat = async () => {
    if (!catLabel) { toast.error("Label requis"); return; }
    setBusy(true);
    try {
      await api.post("/admin/topmada/categories", {
        slug: catSlug || undefined, label: catLabel, kind: catKind, sort_order: catOrder, active: 1,
      });
      toast.success("Catégorie créée");
      setShowCatForm(false); setCatLabel(""); setCatSlug(""); setCatOrder(100);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggleCat = async (slug, active) => {
    try { await api.patch(`/admin/topmada/categories/${slug}`, { active: active ? 0 : 1 }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const renameCat = async (slug, currentLabel) => {
    const newLabel = window.prompt("Nouveau label :", currentLabel);
    if (!newLabel) return;
    try { await api.patch(`/admin/topmada/categories/${slug}`, { label: newLabel }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const deleteCat = async (slug) => {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try { await api.delete(`/admin/topmada/categories/${slug}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  // ── Saisons ──
  const createSeason = async () => {
    if (!seasonLabel || !seasonEnd) { toast.error("Label et date de clôture requis"); return; }
    setBusy(true);
    try {
      await api.post("/admin/topmada/seasons", {
        label: seasonLabel,
        end_at: seasonEnd.replace("T", " ") + ":00",
      });
      toast.success("Nouvelle saison Awards lancée 🎉");
      setSeasonLabel(""); setSeasonEnd("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const closeSeason = async (sid) => {
    if (!window.confirm("Clôturer la saison ? Les gagnants seront archivés définitivement.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/topmada/seasons/${sid}/close`);
      toast.success(`Saison clôturée · ${data.winners.length} gagnant(s) archivé(s) 🏆`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  // ── Recap Hebdo Email ──
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapPreview, setRecapPreview] = useState(null);
  const previewRecap = async () => {
    setRecapBusy(true);
    try {
      const { data } = await api.post("/admin/topmada/send-weekly-recap?preview=1");
      setRecapPreview(data);
      toast.success(`Aperçu prêt · ${data.recipients} destinataires`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Erreur");
    } finally { setRecapBusy(false); }
  };
  const sendRecap = async () => {
    if (!window.confirm("Envoyer maintenant le récap hebdo TopMada à TOUS les utilisateurs opt-in ?")) return;
    setRecapBusy(true);
    try {
      const { data } = await api.post("/admin/topmada/send-weekly-recap");
      toast.success(`Récap envoyé · ${data.sent} emails (${data.errors} erreurs)`);
      setRecapPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Erreur");
    } finally { setRecapBusy(false); }
  };

  // ── Auto-vidéo Vainqueur de la Semaine ──
  const [winnerBusy, setWinnerBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [winnerVideos, setWinnerVideos] = useState([]);
  const loadWinners = async () => {
    try {
      const [s, v] = await Promise.all([
        api.get("/admin/topmada/winner-suggestions"),
        api.get("/admin/topmada/winner-videos"),
      ]);
      setSuggestions(s.data.suggestions || []);
      setWinnerVideos(v.data.videos || []);
    } catch (e) { /* silencieux : pas encore configuré */ }
  };
  useEffect(() => { loadWinners(); }, []);
  const generateWinner = async (sug) => {
    setWinnerBusy(true);
    try {
      const { data } = await api.post("/admin/topmada/winner-video", {
        artist_name: sug.artist_name,
        track_title: sug.track_title,
        category_label: sug.category_label,
        thumbnail_url: sug.thumbnail_url,
        category_slug: sug.category_slug,
        target_type: sug.kind,
        target_id: sug.target_id,
        season_id: currentSeason?.season_id || null,
      });
      toast.success(`Vidéo Vainqueur générée 🏆 (${Math.round((data.bytes || 0) / 1024)} KB)`);
      loadWinners();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Erreur génération");
    } finally { setWinnerBusy(false); }
  };
  const deleteWinner = async (id) => {
    if (!window.confirm("Supprimer cette vidéo ?")) return;
    try {
      await api.delete(`/admin/topmada/winner-video/${id}`);
      loadWinners();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-topmada-panel" className="space-y-6">
      {/* Header */}
      <header className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-500/10 p-5">
        <div className="flex items-center gap-3">
          <Trophy className="text-amber-300" size={28} />
          <div>
            <h2 className="text-xl font-extrabold">Top Mada · Administration</h2>
            <p className="text-sm text-white/65">Catégories de vote + cycle Awards saisonnier. Les utilisateurs votent depuis <code className="text-amber-200">/topmada</code>.</p>
          </div>
        </div>
      </header>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total votes" value={stats.total_votes} icon="🗳️" />
          <StatCard label="Votes 7 jours" value={stats.votes_7d} icon="📈" />
          <StatCard label="Votes 30 jours" value={stats.votes_30d} icon="📊" />
          <StatCard label="Votants uniques" value={stats.unique_voters} icon="👥" />
        </div>
      )}

      {/* Saison Awards courante */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-base font-bold inline-flex items-center gap-2"><Award size={18} className="text-amber-300" /> Saison Awards en cours</h3>
        </div>
        {currentSeason ? (
          <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="text-amber-200 text-xs font-bold uppercase tracking-wider">🟢 Active</p>
              <p className="text-lg font-extrabold mt-0.5">{currentSeason.label}</p>
              <p className="text-xs text-white/55 mt-1">Clôture le {new Date(currentSeason.end_at).toLocaleString("fr-FR")}</p>
            </div>
            <button
              onClick={() => closeSeason(currentSeason.season_id)}
              disabled={busy}
              data-testid="topmada-close-season"
              className="px-4 py-2 rounded-xl bg-rose-500/20 border border-rose-400/40 text-rose-200 text-sm font-bold hover:bg-rose-500/30 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Crown size={14} /> Clôturer & archiver les winners
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <p className="text-sm text-white/65">Aucune saison Awards en cours. Lance une nouvelle saison ci-dessous.</p>
            <div className="grid sm:grid-cols-3 gap-2">
              <input
                type="text" value={seasonLabel} onChange={(e) => setSeasonLabel(e.target.value)}
                placeholder='Ex: "Top Mada Awards · Mada 2026"'
                data-testid="topmada-season-label"
                className="rounded-xl bg-black/30 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400/60"
              />
              <input
                type="datetime-local" value={seasonEnd} onChange={(e) => setSeasonEnd(e.target.value)}
                data-testid="topmada-season-end"
                className="rounded-xl bg-black/30 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400/60"
              />
              <button
                onClick={createSeason}
                disabled={busy}
                data-testid="topmada-create-season"
                className="px-4 py-2.5 rounded-xl bg-amber-400 text-black font-bold hover:brightness-105 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Lancer la saison
              </button>
            </div>
          </div>
        )}
        {seasons.length > 0 && (
          <details className="text-xs text-white/55">
            <summary className="cursor-pointer hover:text-white/80">Historique des saisons ({seasons.length})</summary>
            <ul className="mt-2 space-y-1">
              {seasons.map((s) => (
                <li key={s.season_id} className="flex justify-between gap-2">
                  <span><span className={s.status === 'voting' ? 'text-emerald-300' : 'text-white/40'}>●</span> {s.label}</span>
                  <span className="text-white/40">{new Date(s.start_at).toLocaleDateString("fr-FR")} → {new Date(s.end_at).toLocaleDateString("fr-FR")} · {s.status}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Auto-vidéo Vainqueur de la Semaine */}
      <section className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/5 to-pink-500/5 p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Sparkles className="text-fuchsia-300" size={22} />
          <div className="flex-1">
            <h3 className="text-base font-bold">Vainqueur de la Semaine · Vidéo virale 9:16</h3>
            <p className="text-xs text-white/55 mt-0.5">
              Génère en 1 clic une vidéo TikTok/Reels (8s, format 9:16) à partir du Top 1 de chaque catégorie.
              Prête à partager sur les réseaux pour booster le buzz TopMada.
            </p>
          </div>
          <button
            onClick={loadWinners}
            className="px-2.5 py-1 rounded-full text-[11px] bg-white/[0.05] border border-white/15 hover:bg-white/[0.08]"
            title="Rafraîchir suggestions"
          >
            ↻
          </button>
        </div>

        {suggestions.length === 0 ? (
          <p className="text-xs text-white/45 italic">Aucune suggestion disponible. Importe d&apos;abord des clips dans TopMada.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {suggestions.map((s) => (
              <div key={s.category_slug + s.target_id} data-testid={`winner-sug-${s.category_slug}`} className="rounded-xl bg-black/30 border border-white/10 p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {s.thumbnail_url && (
                    <img src={s.thumbnail_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-white/10" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-fuchsia-200 font-semibold">{s.category_label}</p>
                    <p className="font-bold text-sm truncate">{s.artist_name}</p>
                    {s.track_title && <p className="text-[11px] text-white/55 truncate">{s.track_title}</p>}
                    <p className="text-[10px] text-white/40 mt-0.5">{s.votes} vote{s.votes > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <button
                  onClick={() => generateWinner(s)}
                  disabled={winnerBusy}
                  data-testid={`generate-winner-${s.category_slug}`}
                  className="w-full px-3 py-2 rounded-lg bg-fuchsia-400 text-black text-xs font-black uppercase tracking-wider hover:brightness-105 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                  {winnerBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Générer la vidéo
                </button>
              </div>
            ))}
          </div>
        )}

        {winnerVideos.length > 0 && (
          <div className="pt-3 border-t border-white/10">
            <p className="text-xs font-bold text-white/75 mb-2">📼 Vidéos Vainqueur générées ({winnerVideos.length})</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {winnerVideos.map((w) => (
                <div key={w.id} data-testid={`winner-video-${w.id}`} className="rounded-xl bg-black/30 border border-white/10 p-2 space-y-2">
                  <video
                    src={w.video_url}
                    controls
                    playsInline
                    className="w-full rounded-lg aspect-[9/16] bg-black object-cover"
                  />
                  <p className="text-xs font-bold truncate">{w.artist_name}</p>
                  {w.track_title && <p className="text-[10px] text-white/55 truncate">{w.track_title}</p>}
                  <div className="flex gap-1">
                    <a
                      href={w.video_url}
                      download
                      className="flex-1 px-2 py-1 rounded-lg bg-white/[0.05] border border-white/15 text-[10px] font-bold text-center hover:bg-white/[0.1] inline-flex items-center justify-center gap-1"
                      data-testid={`download-winner-${w.id}`}
                    >
                      <Download size={10} /> MP4
                    </a>
                    <button
                      onClick={() => {
                        const fullUrl = (w.video_url || '').startsWith('http') ? w.video_url : `https://www.voamada.com${w.video_url}`;
                        if (navigator.share) {
                          navigator.share({ title: `🏆 ${w.artist_name} — Vainqueur TopMada`, url: fullUrl });
                        } else {
                          navigator.clipboard.writeText(fullUrl);
                          toast.success("Lien copié");
                        }
                      }}
                      className="flex-1 px-2 py-1 rounded-lg bg-fuchsia-400/15 border border-fuchsia-400/40 text-fuchsia-100 text-[10px] font-bold hover:bg-fuchsia-400/25 inline-flex items-center justify-center gap-1"
                    >
                      <Share2 size={10} /> Partager
                    </button>
                    <button
                      onClick={() => deleteWinner(w.id)}
                      className="px-2 py-1 rounded-lg bg-rose-500/15 border border-rose-400/30 text-rose-200 text-[10px] hover:bg-rose-500/25"
                      title="Supprimer"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Recap Hebdo Email */}
      <section className="rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 p-5 space-y-3">
        <div className="flex items-center gap-3">
          <Mail className="text-cyan-300" size={22} />
          <div className="flex-1">
            <h3 className="text-base font-bold">Récap Hebdo · Email automatisé</h3>
            <p className="text-xs text-white/55 mt-0.5">
              Envoie à tous les utilisateurs opt-in un email avec le Top 3 artistes &amp; Top 3 clips de la semaine.
              Configure le cron OVH sur <code className="text-cyan-200">/api/cron/topmada-weekly-recap?key=YOUR_CRON_KEY</code> chaque dimanche soir.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={previewRecap}
            disabled={recapBusy}
            data-testid="topmada-recap-preview"
            className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/15 text-sm font-bold hover:bg-white/[0.08] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {recapBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Aperçu (sans envoi)
          </button>
          <button
            onClick={sendRecap}
            disabled={recapBusy}
            data-testid="topmada-recap-send"
            className="px-4 py-2 rounded-xl bg-cyan-400 text-black text-sm font-bold hover:brightness-105 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {recapBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer maintenant
          </button>
        </div>
        {recapPreview && (
          <div className="rounded-xl bg-black/40 border border-cyan-400/20 p-3 text-xs space-y-2">
            <p className="text-cyan-200 font-bold">📊 Aperçu · {recapPreview.recipients} destinataires opt-in</p>
            {recapPreview.top_artists?.length > 0 && (
              <div>
                <p className="text-white/65 font-semibold mb-1">🎤 Top {recapPreview.top_artists.length} artistes :</p>
                <ul className="ml-2 space-y-0.5">
                  {recapPreview.top_artists.map((a, i) => (
                    <li key={a.target_id} className="text-white/75">{i + 1}. {a.title} <span className="text-white/40">· {a.votes} votes</span></li>
                  ))}
                </ul>
              </div>
            )}
            {recapPreview.top_tracks?.length > 0 && (
              <div>
                <p className="text-white/65 font-semibold mb-1">🎵 Top {recapPreview.top_tracks.length} clips :</p>
                <ul className="ml-2 space-y-0.5">
                  {recapPreview.top_tracks.map((t, i) => (
                    <li key={t.target_id} className="text-white/75">{i + 1}. {t.title} <span className="text-white/40">{t.artist_name ? '— ' + t.artist_name : ''} · {t.votes} votes</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Catégories */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-base font-bold">Catégories de vote ({cats.length})</h3>
          <button
            onClick={() => setShowCatForm((v) => !v)}
            data-testid="topmada-add-cat"
            className="px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-100 text-xs font-bold hover:bg-amber-400/25 inline-flex items-center gap-1.5"
          >
            <Plus size={12} /> Ajouter
          </button>
        </div>

        {showCatForm && (
          <div className="rounded-xl bg-black/30 border border-white/10 p-3 grid sm:grid-cols-5 gap-2">
            <input type="text" value={catLabel} onChange={(e) => setCatLabel(e.target.value)} placeholder="Label (ex: Meilleur Clip)" data-testid="topmada-cat-label" className="sm:col-span-2 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-amber-400/50" />
            <input type="text" value={catSlug} onChange={(e) => setCatSlug(e.target.value)} placeholder="slug (auto si vide)" data-testid="topmada-cat-slug" className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-amber-400/50" />
            <select value={catKind} onChange={(e) => setCatKind(e.target.value)} data-testid="topmada-cat-kind" className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-amber-400/50">
              {KINDS.map((k) => <option key={k.key} value={k.key} className="bg-black">{k.label}</option>)}
            </select>
            <button onClick={createCat} disabled={busy} data-testid="topmada-cat-create" className="rounded-lg bg-amber-400 text-black font-bold text-sm hover:brightness-105 disabled:opacity-50">Créer</button>
          </div>
        )}

        <ul className="space-y-1.5">
          {cats.map((c) => (
            <li key={c.cat_id} data-testid={`topmada-cat-${c.slug}`} className={`flex items-center gap-3 p-3 rounded-xl border ${c.active ? 'bg-white/[0.03] border-white/10' : 'bg-white/[0.01] border-white/5 opacity-60'}`}>
              <span className="text-2xl">{c.kind === 'artist' ? '🎤' : '🎵'}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">{c.label}</p>
                <p className="text-[11px] text-white/40"><code>{c.slug}</code> · {c.kind} · ordre {c.sort_order}</p>
              </div>
              <button onClick={() => toggleCat(c.slug, c.active)} title={c.active ? "Désactiver" : "Activer"} className="p-2 text-white/60 hover:text-white">
                {c.active ? <Unlock size={14} /> : <Lock size={14} />}
              </button>
              <button onClick={() => renameCat(c.slug, c.label)} title="Renommer" className="p-2 text-white/60 hover:text-amber-300"><Pencil size={14} /></button>
              <button onClick={() => deleteCat(c.slug)} title="Supprimer" className="p-2 text-white/60 hover:text-rose-400"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

const StatCard = ({ label, value, icon }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <p className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">{label}</p>
    <p className="mt-1 text-2xl font-extrabold inline-flex items-center gap-2">
      <span>{icon}</span>{Number(value || 0).toLocaleString("fr-FR")}
    </p>
  </div>
);

export default AdminTopMadaPanel;
